import {
  CalendarIcon,
  LocationIcon,
  MedicalProfessionalMonochromeIcon,
  TimeIcon,
  TrashIcon,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { TLocation } from 'src/types/others'
import { getFullName } from 'src/utils/string-formatter.util'
import { useCurrentPatientFile } from 'src/pages/patients/patient-file/_components/patient-file.provider'
import { twMerge } from 'src/utils/tw-merge.util'
import { TMedicalStaffMember } from 'src/types/medical-staff-member'
import dayjs from 'dayjs'
import {
  getConvocationLabel,
  getDateLabel,
} from 'src/pages/patients/patient-file/patient-followup/_components/patient-med-event.helper'
import { TMedEventUI, TPatientMedEventInteropMetadata } from 'src/types/med-event'
import { TCompletedMedEvent } from '../generic-followup-form.type'
import { TNavigationLocales } from 'src/types/locales'
import TimezoneAwareTime from 'src/components/timezone-aware-time/timezone-aware-time.component'

const DeleteMedEventButton = ({
  medEventLabel,
  completedMedEvent,
  onDelete,
}: {
  medEventLabel: string
  completedMedEvent: TCompletedMedEvent
  onDelete: (medEventToDelete: TCompletedMedEvent) => void
}) => {
  const { t } = useTranslation()
  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    onDelete(completedMedEvent)
  }
  return (
    <button
      className="c-mr-4 c-ml-auto c-my-2 c-h-7 c-w-7 c-min-w-7 c-rounded-full c-bg-grey-700 c-p-1.5 c-text-white hover:c-outline hover:c-outline-4 hover:c-outline-grey-300"
      onClick={onClick}
      aria-label={t('components.generic-followups-form.delete-card-medevent-label', { medEventLabel })}
      type="button"
    >
      <TrashIcon />
    </button>
  )
}

interface MedEventCardHeaderProps {
  medEventLabel: string
  completedMedEvent: TCompletedMedEvent
  onDelete?: (medEventToDelete: TCompletedMedEvent) => void
  doctors: TMedicalStaffMember[]
  locations: TLocation[]
}
const MedEventCardHeader = ({
  medEventLabel,
  completedMedEvent,
  onDelete,
  doctors,
  locations,
}: MedEventCardHeaderProps) => {
  return (
    <div className="c-flex c-items-center c-pt-2">
      <h2 className="c-m-0 c-font-semibold c-px-4 c-border-r-2 c-border-r-grey-200 last-of-type:c-border-none c-flex c-text-base">
        <CalendarIcon className="c-w-5 c-mr-2" />
        {medEventLabel}
      </h2>
      {completedMedEvent.UserId && (
        <p className="c-m-0 c-px-4 c-border-r-2 c-border-r-grey-200 last-of-type:c-border-none c-flex">
          <MedicalProfessionalMonochromeIcon className="c-w-5 c-mr-2" />
          {getFullName(doctors?.find((user) => user.id === completedMedEvent.UserId))}
        </p>
      )}
      {completedMedEvent.LocationId && (
        <p className="c-m-0 c-px-4 c-border-r-2 c-border-r-grey-200 last-of-type:c-border-none c-flex">
          <LocationIcon className="c-w-5 c-mr-2" />
          {locations?.find((location) => location.id === completedMedEvent.LocationId)?.name || ''}
        </p>
      )}
      {onDelete && (
        <DeleteMedEventButton
          medEventLabel={medEventLabel}
          completedMedEvent={completedMedEvent}
          onDelete={onDelete}
        />
      )}
    </div>
  )
}

interface MedEventCardDatesProps {
  medEventUI?: TMedEventUI
  locale?: TNavigationLocales
  rawDate?: Date | string | null
  rawConvocationDate?: Date | string | null
  interopMetadata?: TPatientMedEventInteropMetadata | null
  isPastCard?: boolean
}
const MedEventCardDates = ({
  medEventUI,
  locale,
  rawDate,
  rawConvocationDate,
  interopMetadata,
  isPastCard,
}: MedEventCardDatesProps) => {
  const { t } = useTranslation()

  if (!rawDate) return null

  return (
    <dl className="c-flex c-pb-4">
      <div className="c-px-4 c-border-r-2 c-border-r-grey-200 last-of-type:c-border-none c-flex">
        <TimeIcon className="c-w-5 c-mr-2" />
        <dt className={twMerge('c-mr-2', isPastCard && 'c-text-grey-600')}>
          {`${getDateLabel({ t, medEventUI, locale })} :`}
        </dt>
        <dd className={isPastCard ? 'c-text-grey-700' : 'c-text-secondary-850'}>
          {t('components.generic-followups-form.at-date-prefix')}{' '}
          <TimezoneAwareTime date={rawDate} formatter="dateWithHours" />
        </dd>
      </div>
      {rawConvocationDate && (
        <div className="c-px-4 c-border-r-2 c-border-r-grey-200 last-of-type:c-border-none c-flex">
          <dt className={twMerge('c-mr-2', isPastCard && 'c-text-grey-600')}>
            {`${getConvocationLabel({ t, medEventUI, locale })} :`}
          </dt>
          <dd className={isPastCard ? 'c-text-grey-700' : 'c-text-secondary-850'}>
            {t('components.generic-followups-form.at-date-prefix')}{' '}
            <TimezoneAwareTime date={rawConvocationDate} formatter="dateWithHours" />
          </dd>
        </div>
      )}
      {interopMetadata?.visit?.number && (
        <div className="c-px-4 c-border-r-2 c-border-r-grey-200 last-of-type:c-border-none c-flex">
          <dt className={twMerge('c-mr-2', isPastCard && 'c-text-grey-600')}>
            {`${t('components.generic-followups-form.visit-number')} :`}
          </dt>
          <dd className={isPastCard ? 'c-text-grey-700' : 'c-text-secondary-850'}>
            {interopMetadata.visit.number}
          </dd>
        </div>
      )}
    </dl>
  )
}

interface MedEventCardProps {
  completedMedEvent: TCompletedMedEvent
  onDelete: (medEventToDelete: TCompletedMedEvent) => void
  onClickOnCard?: VoidFunction
}
const MedEventCard = ({ completedMedEvent, onDelete, onClickOnCard }: MedEventCardProps) => {
  const { i18n } = useTranslation()
  const locale = i18n.language as TNavigationLocales
  const { activeDoctors, locations, getMedEventLabelById } = useCurrentProject()

  const medEventLabel = getMedEventLabelById(completedMedEvent.medEventUI?.MedEvent.id)

  if (!onClickOnCard) {
    const isMedEventTooOldToBeDeleted = dayjs(completedMedEvent.date).isBefore(
      dayjs().subtract(7, 'day'),
      'day',
    )
    return (
      <article className="c-min-w-[500px] c-max-w-full c-rounded-lg c-border-2 c-border-solid c-border-grey-200 c-pl-2 c-bg-grey-200 c-cursor-help">
        <MedEventCardHeader
          medEventLabel={medEventLabel}
          completedMedEvent={completedMedEvent}
          onDelete={isMedEventTooOldToBeDeleted ? undefined : onDelete}
          doctors={activeDoctors}
          locations={locations}
        />
        <MedEventCardDates
          medEventUI={completedMedEvent.medEventUI}
          locale={locale}
          rawDate={completedMedEvent.date}
          rawConvocationDate={completedMedEvent.convocationDate}
          interopMetadata={completedMedEvent.interopMetadata}
        />
      </article>
    )
  }

  return (
    <article
      className="c-min-w-[500px] c-max-w-full c-rounded-lg c-border-2 c-border-solid c-border-grey-200 c-bg-white c-cursor-pointer hover:c-shadow-buttonSecondary active:c-border-secondary-400  active:c-shadow-buttonSecondary c-pl-2"
      role="button"
      onClick={onClickOnCard}
    >
      <MedEventCardHeader
        medEventLabel={medEventLabel}
        completedMedEvent={completedMedEvent}
        onDelete={onDelete}
        doctors={activeDoctors}
        locations={locations}
      />
      <MedEventCardDates
        medEventUI={completedMedEvent.medEventUI}
        locale={locale}
        rawDate={completedMedEvent.date}
        rawConvocationDate={completedMedEvent.convocationDate}
        interopMetadata={completedMedEvent.interopMetadata}
      />
    </article>
  )
}

interface DeletedMedEventCardProps {
  completedMedEvent: TCompletedMedEvent
  onDelete: (medEventToDelete: TCompletedMedEvent) => void
}
// This card is when medevent has been deleted from the followup config (from backoffice)
const DeletedMedEventCard = ({ completedMedEvent, onDelete }: DeletedMedEventCardProps) => {
  const { activeDoctors, locations, getMedEventLabelById } = useCurrentProject()
  const { patient } = useCurrentPatientFile()
  const patientMedEvent = patient.PatientMedEvents.find(
    (pme) => pme.id === completedMedEvent.patientMedEventId,
  )
  return (
    <article className="c-min-w-[500px] c-max-w-full c-rounded-lg c-border-2 c-border-solid c-border-grey-200 c-bg-white c-pl-2">
      <MedEventCardHeader
        medEventLabel={getMedEventLabelById(patientMedEvent?.MedEventId)}
        completedMedEvent={completedMedEvent}
        onDelete={onDelete}
        doctors={activeDoctors}
        locations={locations}
      />
      <MedEventCardDates
        rawDate={completedMedEvent.date}
        rawConvocationDate={completedMedEvent.convocationDate}
        interopMetadata={completedMedEvent.interopMetadata}
      />
    </article>
  )
}

export { MedEventCard, DeletedMedEventCard }
